import React from "react";
import {
  DemoResponsiveGrid,
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchMetricCard,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
  ProgressBar,
  type DemoPillVariant,
} from "@csk/demo-ui";
import certifiedManifoldStrataData from "../data/certified-manifold-strata.json";
import {
  BOP_DRIFT_GRADIENT,
  CERTIFIED_MARGINS,
  driftTone,
  summarizeCertifiedManifold,
  type CertifiedRegime,
} from "./certified-manifold-panel/certifiedManifoldModel.js";

const REGIME_COLOR: Record<CertifiedRegime, string> = {
  "REGIME 1": "#22c55e",
  "REGIME 2": "#f59e0b",
  "REGIME 3": "#ef4444",
};

const REGIME_DESCRIPTION: Record<CertifiedRegime, string> = {
  "REGIME 1": "All 10 NTs hold — full structural immunity",
  "REGIME 2": "Some contingent NTs at risk — bounded exposure",
  "REGIME 3": "Budget-constrained mode — active BOP enforcement required",
};

interface ModeStratum {
  readonly mode: string;
  readonly theorems: string;
  readonly status: string;
  readonly variant: DemoPillVariant;
}

const MODE_STRATA =
  certifiedManifoldStrataData.strata as readonly ModeStratum[];

const CertifiedManifoldPanel = (): React.JSX.Element => {
  const state = summarizeCertifiedManifold(
    CERTIFIED_MARGINS,
    BOP_DRIFT_GRADIENT,
  );
  const margins = Object.entries(CERTIFIED_MARGINS);

  return (
    <DemoWorkbenchStack>
      <DemoWorkbenchMetricCard
        label="Regime"
        value={state.regime}
        color={REGIME_COLOR[state.regime]}
        sub={`d_cert = ${state.certificationMargin.toFixed(3)} · ${REGIME_DESCRIPTION[state.regime]}`}
      />

      <DemoWorkbenchCard compact title="Contingent condition margins">
        <DemoWorkbenchStack gap={8}>
          {margins.map(([key, margin]) => (
            <div
              key={key}
              className="grid grid-cols-[150px_1fr_64px] items-center gap-2"
            >
              <span className="text-xs text-gray-400">{margin.name}</span>
              <ProgressBar
                value={margin.value * 100}
                tone={margin.value > 0.5 ? "success" : "warning"}
                height={8}
                ariaLabel={`${margin.name} certification margin`}
              />
              <span className="text-right text-xs text-gray-300">
                {margin.note ?? margin.value.toFixed(2)}
              </span>
            </div>
          ))}
        </DemoWorkbenchStack>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard compact title="∇d_cert — BOP Drift Direction">
        <DemoResponsiveGrid minColumnWidth={120} gap={8}>
          {BOP_DRIFT_GRADIENT.map((entry) => (
            <DemoWorkbenchMetricCard
              key={entry.axis}
              label={entry.axis}
              value={`${entry.drift >= 0 ? "+" : ""}${entry.drift.toFixed(3)}`}
              color={
                driftTone(entry.drift) === "danger"
                  ? "#f87171"
                  : driftTone(entry.drift) === "warning"
                    ? "#fbbf24"
                    : "#34d399"
              }
              sub="per deployment"
            />
          ))}
        </DemoResponsiveGrid>
        <DemoWorkbenchCopy size="xs" tone="muted" className="mt-2">
          Most pressure: {state.worstDrift.axis} (
          {state.worstDrift.drift.toFixed(3)}/deployment)
        </DemoWorkbenchCopy>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard compact>
        <span className="text-teal-400">BOP Conservation:</span> exp(tr(X)) =
        det(exp(X)) — <span className="text-green-400">verified</span>{" "}
        (automatic in GL(n))
      </DemoWorkbenchCard>

      <DemoResponsiveGrid minColumnWidth={160} gap={8}>
        {MODE_STRATA.map((stratum) => (
          <DemoWorkbenchCard
            key={stratum.mode}
            compact
            title={stratum.mode}
            description={stratum.theorems}
            right={
              <DemoWorkbenchPill variant={stratum.variant}>
                {stratum.status}
              </DemoWorkbenchPill>
            }
            style={{ borderColor: `${REGIME_COLOR[state.regime]}55` }}
          >
            <span className="sr-only">Mode stratum status.</span>
          </DemoWorkbenchCard>
        ))}
      </DemoResponsiveGrid>
    </DemoWorkbenchStack>
  );
};

export default CertifiedManifoldPanel;
